import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import {
  saveTradeIn,
  resetTradeInSaveState,
  getTradeIn,
  getTradeInNegativeEquity,
} from '../../ducks/tradeIn.duck';
import { formatCAD } from '../../containers/PaymentCalculator/calculationEngine';
import { Modal, Form, FieldTextInput, FieldSelect, PrimaryButton } from '..';

import css from './TradeInForm.module.css';

const MODAL_ID = 'TradeInForm';

const LOAN_STANDING_OPTIONS = ['paidOff', 'goodStanding', 'behind', 'notSure'];

/**
 * TradeInForm
 *
 * Captures the vehicle a buyer already owns so it can inform a deal: what it
 * is worth, what is still owed on it, and with whom. Saved privately to the
 * user's own profile — this is not a listing and the vehicle is not offered
 * for sale on Skip.
 *
 * Shows the resulting negative equity live, using the same framing as the
 * Finance tab: a payout above the vehicle's value does not disappear, it
 * follows the buyer into the next loan.
 */
export default function TradeInForm({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { saveInProgress, saveError, saved } = useSelector(state => state.tradeIn);
  const currentUser = useSelector(state => state.user.currentUser);
  const existing = getTradeIn(currentUser);

  // Cleared on close, not on open: the result of a save must survive for as
  // long as the form is up (that is when "Saved" is shown), and clearing on
  // the way out still guarantees a clean form next time it is opened.
  useEffect(() => {
    if (!isOpen) dispatch(resetTradeInSaveState());
  }, [isOpen, dispatch]);

  const handleSubmit = values => dispatch(saveTradeIn(values));

  const required = messageId => validators.required(intl.formatMessage({ id: messageId }));

  return (
    <Modal
      id={MODAL_ID}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={(id, disable) => dispatch(manageDisableScrolling(id, disable))}
      usePortal
    >
      <p className={css.title}>
        <FormattedMessage id="TradeInForm.title" />
      </p>
      <p className={css.body}>
        <FormattedMessage id="TradeInForm.body" />
      </p>

      {saved ? (
        <div className={css.saved} role="status">
          <FormattedMessage id="TradeInForm.saved" />
        </div>
      ) : null}

      <FinalForm
        onSubmit={handleSubmit}
        initialValues={existing || {}}
        render={formRenderProps => {
          const { handleSubmit: onFormSubmit, invalid, values } = formRenderProps;
          const negativeEquity = getTradeInNegativeEquity(values);

          return (
            <Form onSubmit={onFormSubmit}>
              <div className={css.row}>
                <FieldTextInput
                  className={css.field}
                  type="text"
                  id="TradeInForm.year"
                  name="year"
                  label={intl.formatMessage({ id: 'TradeInForm.yearLabel' })}
                  validate={required('TradeInForm.yearRequired')}
                />
                <FieldTextInput
                  className={css.field}
                  type="text"
                  id="TradeInForm.make"
                  name="make"
                  label={intl.formatMessage({ id: 'TradeInForm.makeLabel' })}
                  validate={required('TradeInForm.makeRequired')}
                />
                <FieldTextInput
                  className={css.field}
                  type="text"
                  id="TradeInForm.model"
                  name="model"
                  label={intl.formatMessage({ id: 'TradeInForm.modelLabel' })}
                  validate={required('TradeInForm.modelRequired')}
                />
              </div>

              <div className={css.row}>
                <FieldTextInput
                  className={css.field}
                  type="text"
                  id="TradeInForm.vin"
                  name="vin"
                  label={intl.formatMessage({ id: 'TradeInForm.vinLabel' })}
                  placeholder={intl.formatMessage({ id: 'TradeInForm.vinPlaceholder' })}
                />
                <FieldTextInput
                  className={css.field}
                  type="number"
                  id="TradeInForm.odometer"
                  name="odometer"
                  label={intl.formatMessage({ id: 'TradeInForm.odometerLabel' })}
                />
              </div>

              <div className={css.row}>
                <FieldTextInput
                  className={css.field}
                  type="number"
                  id="TradeInForm.estimatedValue"
                  name="estimatedValue"
                  label={intl.formatMessage({ id: 'TradeInForm.estimatedValueLabel' })}
                />
                <FieldTextInput
                  className={css.field}
                  type="number"
                  id="TradeInForm.payout"
                  name="payout"
                  label={intl.formatMessage({ id: 'TradeInForm.payoutLabel' })}
                />
              </div>

              <div className={css.row}>
                <FieldTextInput
                  className={css.field}
                  type="text"
                  id="TradeInForm.lender"
                  name="lender"
                  label={intl.formatMessage({ id: 'TradeInForm.lenderLabel' })}
                />
                <FieldSelect
                  className={css.field}
                  id="TradeInForm.loanStanding"
                  name="loanStanding"
                  label={intl.formatMessage({ id: 'TradeInForm.loanStandingLabel' })}
                >
                  <option disabled value="">
                    {intl.formatMessage({ id: 'TradeInForm.selectPlaceholder' })}
                  </option>
                  {LOAN_STANDING_OPTIONS.map(key => (
                    <option key={key} value={key}>
                      {intl.formatMessage({ id: `TradeInForm.loanStanding.${key}` })}
                    </option>
                  ))}
                </FieldSelect>
              </div>

              {negativeEquity > 0 ? (
                <div className={css.negativeEquity} role="status">
                  <p className={css.negativeEquityAmount}>
                    <FormattedMessage
                      id="TradeInForm.negativeEquityAmount"
                      values={{ amount: formatCAD(negativeEquity) }}
                    />
                  </p>
                  <p className={css.negativeEquityExplainer}>
                    <FormattedMessage id="TradeInForm.negativeEquityExplainer" />
                  </p>
                </div>
              ) : null}

              {saveError ? (
                <div className={css.error} role="status">
                  <FormattedMessage id="TradeInForm.saveError" />
                </div>
              ) : null}

              <p className={css.privacyNote}>
                <FormattedMessage id="TradeInForm.privacyNote" />
              </p>

              <PrimaryButton
                className={css.submitButton}
                type="submit"
                inProgress={saveInProgress}
                disabled={invalid || saveInProgress}
              >
                <FormattedMessage id="TradeInForm.submitButton" />
              </PrimaryButton>
            </Form>
          );
        }}
      />
    </Modal>
  );
}
